import {useCallback} from 'react';
import {setCurrentStep, setFormData} from '@/store/slices/rewardsSlice';
import {publishReward, saveRewardDraft} from '@/services/rewards';
import {REWARD_STATUS} from '@/utils/constants/rewardsConstants';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  validateRewardNameIfNeeded,
  showToast,
  withLoadingAndErrorHandling
} from '@/containers/Rewards/common/hooks/rewardHandlerUtils';
import {useRewardsBase} from '@/containers/Rewards/common/hooks/useRewardsBase';

/**
 * Custom hook for rewards functionality
 * Provides mock data, step navigation, and form management
 *
 * NOTE: This hook is still actively used by:
 * - RewardSetup.jsx
 * - RewardDisplay.jsx
 * - RewardAttributes.jsx
 * - RewardReview.jsx
 * - ActionButtons.jsx
 * - Old CreateReward.jsx (backward compatibility)
 * - Test files
 *
 * The new flow-specific pages (CreateReward, EditReward, ViewReward) use flow-specific hooks directly.
 * This hook maintains the original behavior for shared step components that are used across all flows.
 *
 * Uses useRewardsBase for all common functionality and adds useRewards-specific handlers.
 */
export const useRewards = () => {
  const base = useRewardsBase();
  const {
    currentStep,
    formData,
    dispatch,
    navigate,
    loadDraft,
    buildPayload,
    validateCurrentStep
  } = base;

  // Draft loading when opening /rewards/create/:id is handled by useLoadDraftFromRoute
  // (used in useRewardsEdit and useRewardsView). This hook only provides formData and handlers
  // to step components (RewardSetup, RewardReview, etc.).

  // Handle publishing (needs to be defined before handleNext)
  const handlePublish = useCallback(async () => {
    await withLoadingAndErrorHandling({
      dispatch,
      operation: async () => {
        const payload = buildPayload('PUBLISHED');
        await publishReward(payload);
        showToast(dispatch, 'Reward published successfully', 'success');
        navigate(ROUTES.REWARD_CATALOG);
      },
      onError: () => {
        showToast(dispatch, 'Failed to publish reward', 'error');
      },
      errorMessage: 'Failed to publish reward'
    });
  }, [dispatch, buildPayload, navigate]);

  // Handle next step
  // Handle next step with validation
  const handleNext = useCallback(async () => {
    // For step 1, validate name duplication via API first (only for new rewards, not when editing)
    const nameValidationResult = await validateRewardNameIfNeeded({
      shouldValidate: true,
      currentStep,
      formData
    });

    if (nameValidationResult?.isDuplicateName) {
      return {success: false, isDuplicateName: true};
    }

    if (validateCurrentStep()) {
      if (currentStep < 4) {
        dispatch(setCurrentStep(currentStep + 1));
      } else {
        await handlePublish();
      }
    }
  }, [currentStep, formData, validateCurrentStep, dispatch, handlePublish]);

  // Handle previous step
  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      dispatch(setCurrentStep(currentStep - 1));
    }
  }, [currentStep, dispatch]);

  // Handle draft saving
  // Optional redirectOnSuccess flag lets callers (e.g. inline cards) save without navigating away
  const handleDraft = useCallback(
    async ({redirectOnSuccess = true} = {}) => {
      // For step 1, validate name duplication via API first (only for new rewards, not when editing)
      const nameValidationResult = await validateRewardNameIfNeeded({
        shouldValidate: true,
        currentStep,
        formData
      });

      if (nameValidationResult?.isDuplicateName) {
        return {success: false, isDuplicateName: true};
      }

      await withLoadingAndErrorHandling({
        dispatch,
        operation: async () => {
          const payload = buildPayload(REWARD_STATUS.DRAFT);
          const resp = await saveRewardDraft(payload);
          const respData = resp?.data || resp;
          const returnedId = respData?.id || respData?.rewardId;
          if (
            returnedId &&
            String(returnedId) !== String(formData.rewardId || '')
          ) {
            dispatch(setFormData({rewardId: String(returnedId)}));
          }
          const effectiveId = returnedId || formData?.rewardId;
          if (effectiveId) {
            await loadDraft(effectiveId);
          }
          showToast(dispatch, 'Reward saved as draft successfully', 'success');

          if (redirectOnSuccess) {
            navigate(ROUTES.REWARD_CATALOG);
          }
        },
        onError: () => {
          showToast(dispatch, 'Failed to save draft', 'error');
        },
        errorMessage: 'Failed to save draft'
      });
    },
    [currentStep, formData, dispatch, buildPayload, loadDraft, navigate]
  );

  return {
    ...base,
    // useRewards-specific handlers
    handleNext,
    handlePrevious,
    handleDraft,
    handlePublish,
    // useRewards expects disableDraft as a property, not a function
    disableDraft: base.getDisableDraftFlag()
  };
};
